import React, { useState } from "react";
import {
  MyMessage,
  InputField,
  AddPageShell,
  AddSection,
} from "../../components/Component";
import { codeOk } from "../../../lib/index";
import { useDoSeeDataContext } from "../../../context/DoSeeDataContext";
import { getOneTipsAndAdvice } from "../../../lib/sysNavigation/SysNavigation";
import { FOLDERS, image_kit_folder } from "../utils/Utils";
import { defaultImage } from "../../../lib/libraries/imageKit/ImageKit";
import { ImageKitUpload } from "../../../ui/ImageKitLibrary";
import { useGeoPositionContext } from "../utils/geoPosition";
import { _TYPES_ } from "../utils/types";
import GenerateDescription from "../utils/GenerateDescription";
import {
  VisibilitySection,
  DescriptionSection,
} from "../utils/AddSections";
import { FiImage } from "react-icons/fi";
import { FaLightbulb } from "react-icons/fa";

const Edit = () => {
  const { updateOneContent } = useDoSeeDataContext();
  const tipsAndAdvice: any = getOneTipsAndAdvice();
  const {
    country_id,
    entry_label,
    country_name,
    first_level_id,
    fourth_level_id,
    second_level_id,
    third_level_id,
  } = useGeoPositionContext();

  const [error, setError] = useState(false);
  const [message, setErrorMessage] = useState<any>("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const tips_and_advice_id = tipsAndAdvice?.tips_and_advice_id;
  const [title, setTitle] = useState<any>(tipsAndAdvice?.title);
  const [thumbnail, setThumbnail] = useState<any>(tipsAndAdvice?.thumbnail);
  const [thumbnailId, setThumbnailId] = useState<any>(
    tipsAndAdvice?.image_cdn_id
  );
  const [hashtags, setHashtags] = useState<any>(tipsAndAdvice?.hashtag);
  const [featured, setFeatured] = useState<any>(tipsAndAdvice?.featured);
  const [promoted, setPromoted] = useState<any>(
    tipsAndAdvice?.promoted ? tipsAndAdvice?.promoted : "no"
  );
  const [tipsAndAdviceStatus, setTipsAndAdviceStatus] = useState<any>(
    tipsAndAdvice?.status
  );
  const [description, setDescription] = useState<any>(tipsAndAdvice?.description);
  const [expo, setExpo] = useState<any>(tipsAndAdvice?.expo);
  const tipsAndAdviceRoutes = "/data/tips/advice";
  const [showDescriptionModal, setShowDescriptionModal] = useState<any>(null);

  const responses = (isError: any, message: any) => {
    setError(isError);
    setLoading(false);
    setSuccess(!isError);
    setErrorMessage(message);
  };

  const updateOneTipsAndAdviceHandler = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    if (!thumbnail || !thumbnailId) responses(true, "Image Required");
    else await sendDBRequest();
  };

  const sendDBRequest = async () => {
    const data = {
      country_id: country_id ? country_id : tipsAndAdvice?.country_id,
      first_level_id: first_level_id ? first_level_id : 0,
      second_level_id: second_level_id ? second_level_id : 0,
      third_level_id: third_level_id ? third_level_id : 0,
      fourth_level_id: fourth_level_id ? fourth_level_id : 0,
      title: title,
      slug: title ? title.toLowerCase().replace(/\s/g, "_") : null,
      description: description,
      thumbnail: thumbnail ? thumbnail : defaultImage,
      image_cdn_id: thumbnailId ? thumbnailId : "",
      hashtag: hashtags,
      status: tipsAndAdviceStatus,
      featured: featured,
      promoted: promoted,
      expo: expo,
    };

    const { message, status } = await updateOneContent({
      data_id: tips_and_advice_id,
      data: data,
      content: _TYPES_.TIPSANDADVICE,
      position: {
        country_id,
        first_level_id,
        second_level_id,
        third_level_id,
        fourth_level_id,
      },
    });

    if (status === codeOk) responses(false, message);
    else responses(true, message);
  };

  const clearMessages = () => {
    setError(null as any);
    setSuccess(null as any);
  };

  return (
    <AddPageShell
      backRoute={tipsAndAdviceRoutes}
      eyebrow="DoSee Data • Tips & Advice"
      title="Edit Tips And Advice"
      subtitle={`Update a tip for travellers in ${entry_label || "this destination"}.`}
      icon={<FaLightbulb className="h-5 w-5" />}
      modals={
        showDescriptionModal ? (
          <GenerateDescription
            setDescription={setDescription}
            setShowDescriptionModal={setShowDescriptionModal}
          />
        ) : null
      }
      loading={loading}
      submitLabel="Save Changes"
      onSubmit={updateOneTipsAndAdviceHandler}
      onSubmitClick={() => {
        setError(false);
        setSuccess(false);
        setErrorMessage(null);
      }}
      message={
        <MyMessage
          error={error}
          message={message}
          success={success}
          onExpired={() => {
            setError(false);
            setSuccess(false);
          }}
          onClick={() => {
            setError(false);
            setSuccess(false);
            setErrorMessage("");
          }}
        />
      }
    >
      <AddSection
        title="Cover Image"
        hint="A photo that frames the advice."
        icon={<FiImage className="h-4 w-4" />}
      >
        <ImageKitUpload
          thumbnail={thumbnail}
          thumbnailId={thumbnailId}
          setThumbnail={setThumbnail}
          setThumbnailId={setThumbnailId}
          folder={image_kit_folder(country_name, FOLDERS.TIPS_AND_ADVICES)}
        />
      </AddSection>

      <AddSection title="Tips Details" hint="Title and basics.">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <InputField
            props={{
              id: "title",
              title: "Title",
              type: "text",
              value: title || "",
              onChange: (e: any) => {
                setTitle(e.target.value);
                clearMessages();
              },
            }}
          />
        </div>
      </AddSection>

      <VisibilitySection
        statusId="tipsAndAdvice_status"
        setStatus={setTipsAndAdviceStatus}
        setFeatured={setFeatured}
        setPromoted={setPromoted}
        setExpo={setExpo}
        status={tipsAndAdviceStatus}
        featured={featured}
        promoted={promoted}
        expo={expo}
        clearMessages={clearMessages}
      />

      <DescriptionSection
        description={description}
        setDescription={setDescription}
        hashtags={hashtags}
        setHashtags={setHashtags}
        onGenerate={() => setShowDescriptionModal(true)}
        clearMessages={clearMessages}
      />
    </AddPageShell>
  );
};

export default Edit;
